// Distributors admin — CrediArc staff create/manage the credit-insurance brokers & carriers
// that distribute the platform, and assign each one a book of client tenants. Lives as a tab in
// the Platform Console. Admin-only (the whole console already gates on platformAdmin).
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, Chip, IconButton, CircularProgress, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Autocomplete, Divider, FormControlLabel, Switch,
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { toast } from "react-toastify";
import EmailIcon from "@mui/icons-material/MailOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  getDistributors, createDistributor, updateDistributor, deleteDistributor,
  assignDistributorClient, unassignDistributorClient, inviteDistributor,
} from "./api/distributorsApi";

const INK = "#0B2A4A", MUTED = "#64748b", LINE = "#e3ebf3", BLUE = "#1E6FE8", GREEN = "#3f9a52";

function EditDialog({ open, onClose, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({ name: "", type: "broker", contactEmail: "", logoUrl: "", brokerPlatformEnabled: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ name: initial?.name || "", type: initial?.type || "broker", contactEmail: initial?.contactEmail || "", logoUrl: initial?.logoUrl || "", brokerPlatformEnabled: isEdit ? initial?.brokerPlatformEnabled === true : true });
  }, [open, initial, isEdit]);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const res = isEdit ? await updateDistributor(initial.id, form) : await createDistributor(form);
      toast.success(isEdit ? "Distributor updated" : "Distributor created");
      onSaved(res?.data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.error || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isEdit ? "Edit distributor" : "New distributor"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} size="small" autoFocus />
        <TextField label="Type" select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, brokerPlatformEnabled: e.target.value === "broker" })} size="small">
          <MenuItem value="broker">Broker</MenuItem>
          <MenuItem value="carrier">Carrier</MenuItem>
        </TextField>
        <TextField label="Contact email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} size="small" />
        <TextField label="Logo URL" type="url" value={form.logoUrl} onChange={(e) => setForm({ ...form, logoUrl: e.target.value })} size="small" helperText="Shown in every assigned client's account." />
        {form.type === "broker" && <FormControlLabel control={<Switch checked={form.brokerPlatformEnabled === true} onChange={(e) => setForm({ ...form, brokerPlatformEnabled: e.target.checked })} />} label="Enable full broker management platform" />}
        {form.type === "broker" && <Typography sx={{ mt: -1.25, fontSize: 12, color: MUTED }}>Grants the complete broker workspace and AI review tools.</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={busy}>{isEdit ? "Save" : "Create"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ClientsDialog({ open, onClose, distributor, tenants, onChanged }) {
  const [busy, setBusy] = useState(false);
  const assigned = distributor?.clientTenantIds || [];
  const nameOf = (id) => tenants.find((t) => t.id === id)?.name || id;
  const available = useMemo(
    () => tenants.filter((t) => !assigned.includes(t.id)),
    [tenants, assigned]
  );
  const [pick, setPick] = useState(null);
  const add = async () => {
    if (!pick?.id) return;
    setBusy(true);
    try { const r = await assignDistributorClient(distributor.id, pick.id); onChanged(r?.data); setPick(null); }
    catch (e) { toast.error(e?.response?.data?.error || "Failed to assign"); }
    finally { setBusy(false); }
  };
  const remove = async (tid) => {
    setBusy(true);
    try { const r = await unassignDistributorClient(distributor.id, tid); onChanged(r?.data); }
    catch (e) { toast.error(e?.response?.data?.error || "Failed to unassign"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Clients — {distributor?.name}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Autocomplete
            sx={{ flex: 1 }} size="small" options={available} value={pick}
            getOptionLabel={(o) => o?.name || ""} onChange={(_, v) => setPick(v)}
            renderInput={(p) => <TextField {...p} label="Add a client tenant" />}
          />
          <Button onClick={add} variant="contained" disabled={busy || !pick}>Assign</Button>
        </Box>
        <Divider />
        <Typography sx={{ fontSize: 13, color: MUTED, fontWeight: 700 }}>
          {assigned.length} client{assigned.length === 1 ? "" : "s"}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {assigned.length === 0 && <Typography sx={{ color: MUTED, fontSize: 14 }}>No clients assigned yet.</Typography>}
          {assigned.map((tid) => (
            <Chip key={tid} label={nameOf(tid)} onDelete={() => remove(tid)} deleteIcon={<LinkOffIcon />} sx={{ borderColor: LINE }} variant="outlined" />
          ))}
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Done</Button></DialogActions>
    </Dialog>
  );
}

export default function DistributorsManager({ tenants = [] }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(null);   // {} for new, {id,...} for edit
  const [clientsFor, setClientsFor] = useState(null);

  const load = async () => {
    setError(null);
    try { const r = await getDistributors(); setRows(r?.data || []); }
    catch (e) { setError(e?.response?.data?.error || e.message || "Failed to load distributors"); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const upsertRow = (d) => { if (d) setRows((prev) => { const ex = (prev || []).some((x) => x.id === d.id); return ex ? prev.map((x) => x.id === d.id ? d : x) : [...(prev || []), d]; }); };
  const del = async (d) => {
    if (!window.confirm(`Delete distributor "${d.name}"? Its clients will be unassigned.`)) return;
    try { await deleteDistributor(d.id); setRows((prev) => prev.filter((x) => x.id !== d.id)); toast.success("Distributor deleted"); }
    catch (e) { toast.error(e?.response?.data?.error || "Delete failed"); }
  };

  const [inviting, setInviting] = useState(null);
  const invite = async (d) => {
    if (!d.contactEmail) { toast.error("Add a contact email first (edit the distributor)."); return; }
    setInviting(d.id);
    try {
      const r = await inviteDistributor(d.id);
      upsertRow(r?.data);
      toast.success(`Login + welcome email sent to ${d.contactEmail}`);
    } catch (e) { toast.error(e?.response?.data?.error || "Failed to send invite"); }
    finally { setInviting(null); }
  };

  if (rows === null) return <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={22} /></Box>;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <Typography sx={{ color: MUTED, fontSize: 14 }}>
          Brokers &amp; carriers that distribute CrediArc. Assign each a book of client tenants; they get transparency into those clients&apos; AR risk.
        </Typography>
        <Button onClick={() => setEdit({})} variant="contained" startIcon={<AddBusinessIcon />} sx={{ textTransform: "none", background: BLUE }}>
          New distributor
        </Button>
      </Box>

      {error && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>} sx={{ mb: 2 }}>{error}</Alert>}

      {rows.length === 0 && !error && (
        <Box sx={{ border: `1px dashed ${LINE}`, borderRadius: 2, p: 5, textAlign: "center" }}>
          <Typography sx={{ color: INK, fontWeight: 700, mb: 0.5 }}>No distributors yet</Typography>
          <Typography sx={{ color: MUTED, fontSize: 14 }}>Add a broker or carrier to start distributing the platform.</Typography>
        </Box>
      )}

      <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "12px", overflow: "hidden", background: "#fff" }}>
        <Box sx={{ display: { xs: "none", md: "grid" }, gridTemplateColumns: "2fr .8fr .7fr .7fr 1.6fr 1fr 150px", gap: 1, px: 2, py: 1.15, background: "#f8fafc" }}>
          {["Distributor", "Type", "Clients", "Users", "Contact", "Status", "Actions"].map((label) => (
            <Typography key={label} sx={{ fontSize: 10.5, fontWeight: 750, textTransform: "uppercase", letterSpacing: ".04em", color: "#94a3b8" }}>{label}</Typography>
          ))}
        </Box>
        {rows.map((d) => (
          <Box key={d.id} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr .8fr .7fr .7fr 1.6fr 1fr 150px" }, gap: 1, alignItems: "center", px: 2, py: 1.35, borderTop: `1px solid #f1f5f9`, "&:hover": { background: "#f9fbff" } }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography onClick={() => navigate(`/platform/distributor/${d.id}`)} sx={{ fontSize: 14, fontWeight: 750, color: BLUE, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } }}>{d.name}</Typography>
              <Typography sx={{ fontSize: 11.5, color: "#94a3b8", fontFamily: "monospace" }}>{d.id}</Typography>
              {d.brokerPlatformEnabled === true && <Chip size="small" label="Broker platform" sx={{ mt: .5, height: 19, bgcolor: "#eef2ff", color: "#5b4acb", fontWeight: 700 }} />}
            </Box>
            <Chip size="small" label={d.type === "carrier" ? "Carrier" : "Broker"} sx={{ justifySelf: "start", height: 21, fontWeight: 700, background: d.type === "carrier" ? "#efeafb" : "#e6effb", color: d.type === "carrier" ? "#6b52c0" : BLUE }} />
            <Typography sx={{ fontSize: 13, color: INK, fontWeight: 650 }}>{d.clientTenantIds?.length || 0}</Typography>
            <Typography sx={{ fontSize: 13, color: INK, fontWeight: 650 }}>{new Set([...(d.userIds || []), d.userId].filter(Boolean)).size}</Typography>
            <Typography sx={{ fontSize: 12.5, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.contactEmail || "—"}</Typography>
            <Chip size="small" label={d.status === "inactive" ? "Inactive" : "Active"} sx={{ justifySelf: "start", height: 21, background: d.status === "inactive" ? "#f1f5f9" : "#e8f5eb", color: d.status === "inactive" ? MUTED : GREEN, fontWeight: 700 }} />
            <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, gap: 0.25 }}>
              <Tooltip title="Open distributor page"><IconButton size="small" onClick={() => navigate(`/platform/distributor/${d.id}`)}><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title={d.userId ? "Resend login email" : "Send login + welcome"}><span><IconButton size="small" disabled={inviting === d.id} onClick={() => invite(d)}><EmailIcon fontSize="small" /></IconButton></span></Tooltip>
              <Tooltip title="Manage clients"><IconButton size="small" onClick={() => setClientsFor(d)}><AddBusinessIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Edit"><IconButton size="small" onClick={() => setEdit(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Delete"><IconButton size="small" onClick={() => del(d)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
          </Box>
        ))}
      </Box>

      <EditDialog open={!!edit} initial={edit} onClose={() => setEdit(null)} onSaved={upsertRow} />
      <ClientsDialog open={!!clientsFor} distributor={clientsFor} tenants={tenants}
        onClose={() => setClientsFor(null)} onChanged={(d) => { upsertRow(d); setClientsFor(d); }} />
    </Box>
  );
}
